/**
 * The team attendance report — V6-04 §4.
 *
 * People × days, in the viewer's scope and no wider. Two columns of hours sit
 * side by side and are never added together: **attendance** is presence and
 * **tracked** is task-timer time. They answer different questions, they
 * disagree constantly and legitimately, and a single merged "hours" number is
 * one somebody would eventually take to a payroll conversation.
 */

import { useState } from 'react';
import { Download, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { AttendanceDayStatus } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { AttendanceTabs } from './AttendanceTabs';
import { currentMonth } from './attendanceMonth';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { UserLink } from '@/components/shared/UserLink';
import { cn } from '@/lib/utils';
import { STATUS_STYLE, monthLabel } from './MyAttendancePage';
import { cellHoursLabel, showsHours } from './teamCell';
import { downloadTeamCsv, useTeamAttendance, useWhoIsIn } from './api';

const CELL: Record<AttendanceDayStatus, string> = {
  present: 'bg-[var(--accent-soft)] text-[var(--accent)]',
  half_day: 'bg-[color-mix(in_srgb,var(--warn)_20%,transparent)] text-[var(--warn)]',
  absent: 'bg-[color-mix(in_srgb,var(--danger)_20%,transparent)] text-[var(--danger)]',
  leave: 'bg-[var(--surface-2)]',
  holiday: 'bg-[var(--surface-2)] vt-muted',
  non_working: 'vt-muted',
};

/** One letter per verdict; the legend below the grid spells them out. */
const LETTER: Record<AttendanceDayStatus, string> = {
  present: 'P',
  half_day: 'H',
  absent: 'A',
  leave: 'L',
  holiday: 'X',
  non_working: '·',
};

function shiftMonth(month: string, by: number): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthNumber - 1 + by, 1)).toISOString().slice(0, 7);
}

function Grid({ month }: { month: string }) {
  const { data, isLoading } = useTeamAttendance(month);

  if (isLoading) return <Skeleton className="h-64" />;
  if (!data || data.rows.length === 0) {
    return (
      /**
       * V9-04 §2 — an empty grid must say what makes it fill.
       *
       * "Nothing here yet" reads as broken to somebody who knows their team
       * exists, which is exactly what the founder reported. The rows come from
       * the people the viewer may see, so an empty grid means the viewer leads
       * nobody — a different fact from "nobody has clocked in", and worth
       * saying as such.
       */
      <EmptyState
        icon={<Users className="size-6" />}
        title="Nobody to show"
        description="This report covers the people you lead, and there is nobody in that scope yet. Attendance fills in as people press Start work."
      />
    );
  }

  return (
    <div className="vt-surface overflow-x-auto" data-testid="team-attendance-grid">
      <table className="min-w-full border-collapse text-[12px]">
        <thead>
          <tr className="border-b border-[var(--border)]">
            <th scope="col" className="sticky left-0 z-10 bg-[var(--surface)] px-2 py-1.5 text-left font-medium">
              Person
            </th>
            {data.dates.map((date) => (
              <th key={date} scope="col" className="vt-mono px-1 py-1.5 text-center font-normal vt-muted">
                {date.slice(-2)}
              </th>
            ))}
            <th className="px-2 text-right font-medium">P</th>
            <th className="px-2 text-right font-medium">H</th>
            <th className="px-2 text-right font-medium">A</th>
            <th className="px-2 text-right font-medium">L</th>
            <th className="px-2 text-right font-medium">Late</th>
            <th className="px-2 text-right font-medium" title="Presence, from attendance sessions">
              Attend h
            </th>
            <th className="px-2 text-right font-medium" title="Tracked work, from task timers">
              Tracked h
            </th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr
              key={row.userId}
              className="border-b border-[var(--border)] last:border-0"
              data-testid="team-row"
              data-user-id={row.userId}
              data-name={row.name}
            >
              <th scope="row" className="sticky left-0 z-10 bg-[var(--surface)] px-2 py-1 text-left font-normal">
                <UserLink userId={row.userId} name={row.name} avatarUrl={row.avatarUrl ?? undefined} size={20} />
              </th>
              {data.dates.map((date) => {
                const cell = row.days[date];
                const hasHours = showsHours(cell?.status);
                return (
                  <td
                    key={date}
                    className={cn('px-1 py-1 text-center align-middle', cell && CELL[cell.status])}
                    title={
                      cell
                        ? `${date} — ${STATUS_STYLE[cell.status].label}${
                            hasHours ? ` · ${cellHoursLabel(cell.minutes)}` : ''
                          }${cell.provisional ? ' (provisional until tonight’s finalization)' : ''}`
                        : date
                    }
                    data-testid="team-cell"
                    data-date={date}
                    data-status={cell?.status}
                    data-provisional={cell?.provisional ? 'true' : undefined}
                    data-hours={hasHours ? cellHoursLabel(cell!.minutes) : undefined}
                  >
                    {/**
                     * V10 §3 — the letter, and the day's hours beneath it.
                     *
                     * **Both lines always render**, and the second is a
                     * non-breaking space when there are no hours to show. That
                     * is the whole of the geometry requirement: a cell's height
                     * is the same whether or not it has hours, so a row of
                     * Sundays and a row of full days sit on the same baseline
                     * and the grid does not ripple as the month fills in.
                     */}
                    <span className="flex flex-col items-center leading-[1.15]">
                      <span
                        className={cn(
                          /**
                           * V9-04 §2 — "the month grid includes today's
                           * provisional cell with a subtle marker".
                           *
                           * On the letter rather than the cell now that the
                           * cell has two lines: underlining the hours as well
                           * would read as a link, and the marker belongs to
                           * the verdict that is not settled.
                           */
                          cell?.provisional && 'underline decoration-dotted underline-offset-2',
                        )}
                      >
                        {cell ? LETTER[cell.status] : ' '}
                      </span>
                      <span
                        className="vt-mono text-[9px] tabular-nums opacity-70"
                        data-testid="team-cell-hours"
                        aria-hidden={!hasHours}
                      >
                        {hasHours ? cellHoursLabel(cell!.minutes) : ' '}
                      </span>
                    </span>
                  </td>
                );
              })}
              <td className="vt-mono px-2 text-right" data-testid="total-present">{row.totals.present}</td>
              <td className="vt-mono px-2 text-right">{row.totals.halfDay}</td>
              <td className="vt-mono px-2 text-right" data-testid="total-absent">{row.totals.absent}</td>
              <td className="vt-mono px-2 text-right">{row.totals.leave}</td>
              <td className="vt-mono px-2 text-right">{row.totals.lates}</td>
              <td className="vt-mono px-2 text-right" data-testid="total-attendance-hours">
                {(row.totals.attendanceMinutes / 60).toFixed(1)}
              </td>
              <td className="vt-mono px-2 text-right vt-muted" data-testid="total-tracked-hours">
                {(row.totals.trackedMinutes / 60).toFixed(1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="px-2 py-2 text-[11px] vt-muted">
        P present · H half day · A absent · L leave · X holiday · · non-working. The figure under a
        P or an H is that day’s attendance hours. “Attend h” is presence; “Tracked h” is task-timer
        time. They are different measurements and are never added together.
      </p>
    </div>
  );
}

function Today() {
  const { data = [], isLoading } = useWhoIsIn();
  if (isLoading) return <Skeleton className="h-40" />;

  const working = data.filter((row) => row.status === 'working');
  const notStarted = data.filter((row) => row.status === 'not_started');

  /**
   * V9-04 §2 — an honest empty state for a workspace where nobody has begun.
   *
   * A blank page is indistinguishable from a broken one, which is exactly the
   * complaint this document exists to answer. If there is nobody to show at
   * all, say why and what makes people appear.
   */
  if (data.length === 0) {
    return (
      <EmptyState
        icon={<Users className="size-6" />}
        title="Nobody to show yet"
        description="Attendance fills in as people press Start work — nobody has yet today."
      />
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="who-is-in">
      <p className="text-[12px] vt-muted">
        {working.length} of {data.length} working right now.
        {/* Everything on this tab is live and unfinalized; say so once. */}
        <span className="ml-1">Today is provisional until tonight’s finalization.</span>
      </p>

      {/**
       * §2 — the "not started today" strip.
       *
       * Working-day aware by construction rather than by a second rule: the
       * server only returns rows for a day it considers workable, so a holiday
       * or somebody's leave day produces no row here and nobody is listed as
       * "not started" on a day they were never expected.
       */}
      {notStarted.length > 0 && (
        <p
          className="rounded-[6px] bg-[var(--surface-2)] px-3 py-1.5 text-[12px]"
          data-testid="not-started-strip"
        >
          <span className="font-medium">Not started today:</span>{' '}
          {notStarted.map((row) => row.name).join(', ')}
        </p>
      )}
      <ul className="vt-surface divide-y divide-[var(--border)]">
        {data.map((row) => (
          <li
            key={row.userId}
            className="flex items-center gap-2 px-3 py-1.5 text-[13px]"
            data-testid="who-is-in-row"
            data-status={row.status}
          >
            <UserLink userId={row.userId} name={row.name} avatarUrl={row.avatarUrl ?? undefined} size={22} />
            <span className="ml-auto vt-mono text-[12px] vt-muted">
              {row.startedAt
                ? new Date(row.startedAt).toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—'}
            </span>
            <span
              className={cn(
                'rounded-full px-1.5 py-0.5 text-[11px]',
                row.status === 'working'
                  ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'bg-[var(--surface-2)] vt-muted',
              )}
            >
              {row.status === 'working' ? 'working' : row.status === 'finished' ? 'finished' : 'not in'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TeamAttendancePage() {
  const [month, setMonth] = useState(() => currentMonth());
  const [tab, setTab] = useState<'month' | 'today'>('month');

  const [exporting, setExporting] = useState(false);

  async function exportCsv(): Promise<void> {
    setExporting(true);
    try {
      await downloadTeamCsv(month);
    } catch {
      toast.error('Could not export the month');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      {/* V9-04 §2 — the Team view is one click away, from either page. */}
      <AttendanceTabs />

      <PageHeader
        title="Team Attendance"
        subtitle={tab === 'month' ? monthLabel(month) : 'Today'}
        actions={
          <div className="flex items-center gap-1">
            {tab === 'month' && (
              <>
                <Button variant="ghost" size="sm" data-testid="team-month-prev" onClick={() => setMonth(shiftMonth(month, -1))}>
                  ‹
                </Button>
                <span className="vt-mono text-[12px]" data-testid="team-month-label">
                  {month}
                </span>
                <Button variant="ghost" size="sm" data-testid="team-month-next" onClick={() => setMonth(shiftMonth(month, 1))}>
                  ›
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="team-export"
                  disabled={exporting}
                  onClick={() => void exportCsv()}
                >
                  {exporting ? <Loader2 className="animate-spin" /> : <Download />}
                  CSV
                </Button>
              </>
            )}
          </div>
        }
        toolbar={
          <div className="flex gap-1" role="tablist">
            <Button
              variant={tab === 'month' ? 'subtle' : 'ghost'}
              size="sm"
              role="tab"
              aria-selected={tab === 'month'}
              data-testid="tab-month"
              onClick={() => setTab('month')}
            >
              Month
            </Button>
            <Button
              variant={tab === 'today' ? 'subtle' : 'ghost'}
              size="sm"
              role="tab"
              aria-selected={tab === 'today'}
              data-testid="tab-today"
              onClick={() => setTab('today')}
            >
              Today
            </Button>
          </div>
        }
      />

      <div className="p-4">{tab === 'month' ? <Grid month={month} /> : <Today />}</div>
    </>
  );
}
